import {
  AccountCircle as AccountCircleIcon,
  AddCircle as AddCircleIcon,
  FolderSharedOutlined as FileSharingIcon,
  Logout as LogoutIcon,
  ScheduleOutlined as TimesheetIcon,
  TaskAltOutlined as TodosIcon,
} from '@mui/icons-material'
import {
  Avatar,
  alpha,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import { LocaleSelector } from '@/components/LocaleSelector/LocaleSelector'
import { MenuItemLink } from '@/helpers/compositions/MenuItemLink'
import { T } from '@/helpers/T'
import { useSWRAxios } from '@/hooks/useSWRAxios'
import { useUserAvatar } from '@/hooks/useUserAvatar'
import type { ITodoCounts } from '@/modules/Todo/Todo'
import { useAuthContext } from '@/providers/Global'
import { hasBaseRole, isMemberOnly } from '@/utils/authorities'

import { NavbarUserAnother } from './Navbar.user.another'
import { NavbarUserImpersonate } from './Navbar.user.impersonate'

//	///////////////////////
//	MARK: EXPORTS
const NavbarUserInternal: React.FC = () => {
  const Auth = useAuthContext()

  const avatar = useUserAvatar()

  // Rôles non étendus par la hiérarchie : voir BASE_ROLE_HOLDERS. Un membre seul n'a ni tâches ni feuille de temps.
  const roles = Auth.active?.roles ?? []
  const staff = hasBaseRole(roles) && !isMemberOnly(roles)

  const todoCounts = useSWRAxios<ITodoCounts>(staff ? '/api/todo/counts' : false, {
    refreshInterval: 60_000,
  })
  const todoBadge = todoCounts.data?.data.targetedOpen ?? 0

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)

  const handleOpenUserMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }, [])

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null)
  }, [])

  const logout = useCallback(() => Auth.logout(), [Auth])

  const addUser = useCallback(() => Auth.changeSession(), [Auth])

  const entityId = Auth.active!.globalEntity?.eapcID

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: { xs: 0.5, md: 1 },
        borderLeft: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
      }}
    >
      <Tooltip title={<T id='lang.open_menu' />}>
        <span>
          <IconButton
            onClick={handleOpenUserMenu}
            sx={{
              p: 0.5,
              ml: 0.5,
              borderRadius: '50%',
              transition: 'transform 150ms ease, box-shadow 150ms ease',
              '&:hover': {
                transform: 'scale(1.04)',
              },
              '& .MuiAvatar-root': {
                border: (theme) => `2px solid ${alpha(theme.palette.common.white, 0.7)}`,
                boxShadow: (theme) => `0 0 0 1px ${alpha(theme.palette.common.black, 0.25)}`,
              },
            }}
          >
            <Badge color='error' overlap='circular' badgeContent={todoBadge}>
              <Avatar
                alt={Auth.active!.user.fullName}
                src={avatar}
                sx={{ width: 36, height: 36 }}
              />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorElUser}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={!!anchorElUser}
        onClose={handleCloseUserMenu}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              overflow: 'hidden',
              mt: 1.25,
              minWidth: 300,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            m: 1,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              p: 0.5,
            }}
          >
            <Avatar alt={Auth.active!.user.fullName} src={avatar} sx={{ width: 48, height: 48 }} />
            <Box sx={{ minWidth: 0, flexGrow: 1, ml: 2 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  lineHeight: 1.25,
                }}
              >
                {Auth.active!.user.fullName}
              </Typography>
              <Typography
                variant='caption'
                noWrap
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                }}
              >
                {Auth.active!.username}
              </Typography>
              {entityId && (
                <Chip
                  size='small'
                  label={entityId}
                  sx={{
                    mt: 0.5,
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              )}
            </Box>
          </Box>
          <Divider sx={{ opacity: 0.4 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <LocaleSelector variant='segmented' />
          </Box>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItemLink to='/dashboard/profile' onClick={handleCloseUserMenu} sx={{ py: 1 }}>
          <ListItemIcon>
            <AccountCircleIcon fontSize='small' />
          </ListItemIcon>
          <T id='lang.your_profile' />
        </MenuItemLink>
        {staff && (
          <MenuItemLink to='/dashboard/time' onClick={handleCloseUserMenu} sx={{ py: 1 }}>
            <ListItemIcon>
              <TimesheetIcon fontSize='small' />
            </ListItemIcon>
            <T id='m.time.my.title' />
          </MenuItemLink>
        )}
        {staff && (
          <MenuItemLink to='/todos' onClick={handleCloseUserMenu} sx={{ py: 1 }}>
            <ListItemIcon>
              <TodosIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              <T id='lang.todos' />
            </ListItemText>
            {todoBadge > 0 && (
              <Chip size='small' color='error' label={todoBadge} sx={{ ml: 2, height: 20 }} />
            )}
          </MenuItemLink>
        )}
        {staff && (
          <MenuItemLink to='/dashboard/file-sharing' onClick={handleCloseUserMenu} sx={{ py: 1 }}>
            <ListItemIcon>
              <FileSharingIcon fontSize='small' />
            </ListItemIcon>
            <T id='m.file_sharing.title' />
          </MenuItemLink>
        )}
        <MenuItem onClick={logout} sx={{ py: 1 }}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          <T id='lang.logout' />
        </MenuItem>
        <MenuItem onClick={addUser} sx={{ py: 1 }}>
          <ListItemIcon>
            <AddCircleIcon fontSize='small' />
          </ListItemIcon>
          <T id='lang.add_an_account' />
        </MenuItem>
        <NavbarUserImpersonate onClose={handleCloseUserMenu} />
        {Auth.sessions.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {Auth.sessions.map((session) => (
          <NavbarUserAnother key={session.username} session={session} />
        ))}
      </Menu>
    </Box>
  )
}

export const NavbarUser = React.memo(NavbarUserInternal) as typeof NavbarUserInternal
